import { err, ok, serverError, type Result } from "./result.ts";
import type {
  Answer,
  Author,
  Capability,
  ChangeEvent,
  Customer,
  DraftAnswer,
  DraftRelevancy,
  EvaluationRequest,
  FillView,
  IssuedToken,
  Me,
  Persona,
  Product,
  Question,
  Questionnaire,
  Relevancy,
  RelevancyFillView,
  RelevancyQuestionnaire,
  RelevancyRating,
  Report,
  ReportSnapshot,
  Requirement,
  Topic,
  TreeTopic,
  User,
  View,
} from "./domain.ts";

/** Where the API lives; the same prefix the server nests its routes under. */
export const apiPrefix = "/api/v1";

/**
 * What an authenticated caller sends along. The session itself travels as a
 * cookie the server sets, so a logged-in browser carries no key at all; the API
 * key is here for the login exchange and for non-browser callers.
 */
export type Session = {
  apiKey?: string;
  /** The author a bootstrap write is attributed to in the change log. */
  actor?: string;
  /**
   * The customer an admin is previewing. The server narrows every read to that
   * customer's world and refuses every write while the header is present.
   */
  previewCustomer?: string;
};

/** Where the browser remembers a running preview; per tab, gone when it closes. */
export const previewStorageKey = "cms_preview_customer";

/** The customer under preview in this tab, if any. */
export const readPreview = (): string | undefined => {
  try {
    return sessionStorage.getItem(previewStorageKey) ?? undefined;
  } catch {
    return undefined;
  }
};

/** Starts or ends a preview; `undefined` ends it. */
export const writePreview = (customer: string | undefined): void => {
  try {
    if (customer === undefined) sessionStorage.removeItem(previewStorageKey);
    else sessionStorage.setItem(previewStorageKey, customer);
  } catch {
    // Without storage the preview simply does not survive a reload.
  }
};

/** The read parameters every query route accepts. */
export type Reads = {
  lang?: string;
  as_of?: string;
};

/** The collections that share the standard entity shape, by path segment. */
export type Collections = {
  topics: Topic;
  products: Product;
  capabilities: Capability;
  questions: Question;
  answers: Answer;
  authors: Author;
  personas: Persona;
  requirements: Requirement;
  views: View;
  questionnaires: Questionnaire;
  relevancies: Relevancy;
  "relevancy-questionnaires": RelevancyQuestionnaire;
  reports: Report;
  customers: Customer;
  users: User;
};

export type Collection = keyof Collections;

export type TreeQuery = Reads & {
  view?: string;
  topics?: string;
  depth?: number;
};

export type HistoryQuery = {
  as_of?: string;
  limit?: number;
  offset?: number;
};

export type Api = {
  /** Exchanges a personal access token, or the bootstrap key, for a session cookie. */
  login: (token: string) => Promise<Result<{ authorized: boolean }>>;
  logout: () => Promise<Result<unknown>>;
  /** Who the caller is; the shell picks the navigation from this. */
  me: () => Promise<Result<Me>>;
  /** Writes a user and mints their token; the token comes back exactly once. */
  createUser: (user: Partial<User>) => Promise<Result<IssuedToken>>;
  /** Mints a new token for a user, ending the old one and every session. */
  rotateToken: (id: string) => Promise<Result<IssuedToken>>;
  /** Revokes a user's token and ends their sessions; the row stays, inactive. */
  revokeToken: (id: string) => Promise<Result<User>>;
  list: <C extends Collection>(collection: C, reads?: Reads) => Promise<Result<Collections[C][]>>;
  get: <C extends Collection>(
    collection: C,
    id: string,
    reads?: Reads,
  ) => Promise<Result<Collections[C]>>;
  create: <C extends Collection>(
    collection: C,
    entity: Collections[C],
  ) => Promise<Result<Collections[C]>>;
  patch: <C extends Collection>(
    collection: C,
    id: string,
    fields: Partial<Collections[C]>,
  ) => Promise<Result<Collections[C]>>;
  /**
   * Deletes one entity. Without `force` the server refuses while anything still
   * names it and answers an `in_use` envelope listing what; with it, referrers
   * that cannot exist without the entity go too and the rest are rewritten.
   */
  remove: <C extends Collection>(
    collection: C,
    id: string,
    options?: { force?: boolean },
  ) => Promise<Result<null>>;
  tree: (query?: TreeQuery) => Promise<Result<TreeTopic[]>>;
  history: (
    entityType: string,
    id: string,
    query?: HistoryQuery,
  ) => Promise<Result<ChangeEvent[]>>;
  evaluate: (request: EvaluationRequest) => Promise<Result<ReportSnapshot>>;
  createReport: (request: EvaluationRequest) => Promise<Result<Report>>;
  lockReport: (id: string) => Promise<Result<Report>>;
  reportEvaluation: (id: string) => Promise<Result<ReportSnapshot>>;
  acceptQuestionnaire: (id: string) => Promise<Result<Answer[]>>;
  acceptRelevancy: (id: string) => Promise<Result<Relevancy[]>>;
  fill: (token: string, reads?: Reads) => Promise<Result<FillView>>;
  /** The same view for an analyst, which leaves the questionnaire's stamps alone. */
  review: (id: string, reads?: Reads) => Promise<Result<FillView>>;
  saveDraft: (
    token: string,
    question: string,
    body: DraftBody,
  ) => Promise<Result<DraftAnswer>>;
  submitFill: (token: string) => Promise<Result<Questionnaire>>;
  /** The relevancy magic link: the persona, its capabilities and the drafts. */
  fillRelevancy: (token: string, reads?: Reads) => Promise<Result<RelevancyFillView>>;
  /** The same view for an analyst, which leaves the questionnaire's stamps alone. */
  reviewRelevancy: (id: string, reads?: Reads) => Promise<Result<RelevancyFillView>>;
  saveRating: (
    token: string,
    capability: string,
    body: RatingBody,
  ) => Promise<Result<DraftRelevancy>>;
  submitRelevancy: (token: string) => Promise<Result<RelevancyQuestionnaire>>;
  /**
   * Where a workbook download lives. A workbook is bytes, not a `Result`, so it
   * travels as a plain URL the browser fetches with the session cookie already
   * attached, instead of through the JSON request path.
   */
  workbookUrl: (
    kind: "questionnaires" | "reports",
    id: string,
    params?: { lang?: string; product?: string },
  ) => string;
};

/**
 * One answer as the recipient enters it. No product: the questionnaire names the
 * one it covers, and the recipient is never told that others exist.
 */
export type DraftBody = {
  value?: DraftAnswer["value"];
  comment?: string | null;
  explanation?: string | null;
};

/**
 * One rating as the recipient leaves it. Both fields are optional and an absent
 * one keeps what is stored, so a pick and a note can be saved on their own
 * clocks; `note: null` clears the note.
 */
export type RatingBody = {
  rating?: RelevancyRating;
  note?: string | null;
};

type RequestSpec = {
  method: "GET" | "PUT" | "POST" | "PATCH" | "DELETE";
  path: string;
  query?: Record<string, string | number | undefined>;
  body?: unknown;
};

const joinUrl = (baseUrl: string, path: string): string =>
  `${baseUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;

/** Only the parameters that carry a value reach the URL. */
const withQuery = (url: string, query: RequestSpec["query"]): string => {
  const entries = Object.entries(query ?? {}).filter(
    (entry): entry is [string, string | number] => entry[1] !== undefined,
  );
  if (entries.length === 0) return url;

  const search = new URLSearchParams(entries.map(([key, value]) => [key, String(value)]));
  return `${url}?${search.toString()}`;
};

const readBody = async (response: Response): Promise<Result<unknown>> => {
  if (response.status === 204) return ok(null);
  const text = await response.text();
  if (text.length === 0) return ok(null);
  try {
    return ok(JSON.parse(text) as unknown);
  } catch {
    return err({ kind: "decode", message: "Response was not valid JSON" });
  }
};

/**
 * Injects transport context by currying, so no module imports a live client.
 * The returned function never throws: transport faults become ApiError values.
 */
export const makeRequest =
  (baseUrl: string, getSession: () => Session, doFetch: typeof fetch = fetch) =>
  async <T>(spec: RequestSpec): Promise<Result<T>> => {
    const { apiKey, actor, previewCustomer } = getSession();

    const response = await doFetch(withQuery(joinUrl(baseUrl, spec.path), spec.query), {
      method: spec.method,
      credentials: "same-origin",
      headers: {
        accept: "application/json",
        ...(spec.body === undefined ? {} : { "content-type": "application/json" }),
        ...(apiKey === undefined ? {} : { "x-api-key": apiKey }),
        ...(actor === undefined ? {} : { "x-actor": actor }),
        ...(previewCustomer === undefined ? {} : { "x-preview-customer": previewCustomer }),
      },
      body: spec.body === undefined ? undefined : JSON.stringify(spec.body),
    }).catch((cause: unknown) => (cause instanceof Error ? cause : new Error(String(cause))));

    if (response instanceof Error) return err({ kind: "network", message: response.message });

    const body = await readBody(response);
    if (!body.ok) return body;
    if (!response.ok) return err(serverError(response.status, body.value));

    return ok(body.value as T);
  };

/** The typed endpoint surface the components receive through context. */
export const makeApi = (
  baseUrl: string = apiPrefix,
  getSession: () => Session = () => ({ previewCustomer: readPreview() }),
  doFetch: typeof fetch = fetch,
): Api => {
  const request = makeRequest(baseUrl, getSession, doFetch);

  return {
    login: (token) => request({ method: "POST", path: "/login", body: { token } }),
    logout: () => request({ method: "POST", path: "/logout" }),
    me: () => request({ method: "GET", path: "/me" }),
    createUser: (user) => request({ method: "POST", path: "/users", body: user }),
    rotateToken: (id) => request({ method: "POST", path: `/users/${id}/rotate` }),
    revokeToken: (id) => request({ method: "POST", path: `/users/${id}/revoke` }),

    list: (collection, reads) =>
      request({ method: "GET", path: `/${collection}`, query: { ...reads } }),
    get: (collection, id, reads) =>
      request({ method: "GET", path: `/${collection}/${id}`, query: { ...reads } }),
    create: (collection, entity) =>
      request({ method: "POST", path: `/${collection}`, body: entity }),
    patch: (collection, id, fields) =>
      request({ method: "PATCH", path: `/${collection}/${id}`, body: fields }),
    remove: (collection, id, options) =>
      request({
        method: "DELETE",
        path: `/${collection}/${id}`,
        query: options?.force === true ? { force: "true" } : {},
      }),

    tree: (query) => request({ method: "GET", path: "/tree", query: { ...query } }),
    history: (entityType, id, query) =>
      request({
        method: "GET",
        path: `/entities/${entityType}/${id}/history`,
        query: { ...query },
      }),

    evaluate: (body) => request({ method: "POST", path: "/evaluate", body }),
    createReport: (body) => request({ method: "POST", path: "/reports", body }),
    lockReport: (id) => request({ method: "POST", path: `/reports/${id}/lock` }),
    reportEvaluation: (id) => request({ method: "GET", path: `/reports/${id}` }),
    acceptQuestionnaire: (id) =>
      request({ method: "POST", path: `/questionnaires/${id}/accept` }),
    acceptRelevancy: (id) =>
      request({ method: "POST", path: `/relevancy-questionnaires/${id}/accept` }),

    fill: (token, reads) =>
      request({ method: "GET", path: `/questionnaires/fill/${token}`, query: { ...reads } }),
    review: (id, reads) =>
      request({ method: "GET", path: `/questionnaires/${id}/review`, query: { ...reads } }),
    saveDraft: (token, question, body) =>
      request({
        method: "PUT",
        path: `/questionnaires/fill/${token}/answers/${question}`,
        body,
      }),
    submitFill: (token) =>
      request({ method: "POST", path: `/questionnaires/fill/${token}/submit` }),

    fillRelevancy: (token, reads) =>
      request({ method: "GET", path: `/relevancy/fill/${token}`, query: { ...reads } }),
    reviewRelevancy: (id, reads) =>
      request({
        method: "GET",
        path: `/relevancy-questionnaires/${id}/review`,
        query: { ...reads },
      }),
    saveRating: (token, capability, body) =>
      request({
        method: "PUT",
        path: `/relevancy/fill/${token}/ratings/${capability}`,
        body,
      }),
    submitRelevancy: (token) =>
      request({ method: "POST", path: `/relevancy/fill/${token}/submit` }),

    workbookUrl: (kind, id, params) =>
      withQuery(joinUrl(baseUrl, `/${kind}/${id}/xlsx`), { ...params }),
  };
};
